import type { ReactNode } from 'react';
import { cx } from '../lib/cx';
import { Icon, type IconName } from './Icon';

export function Spinner({ size = 20, className }: { size?: number; className?: string }) {
  return (
    <svg className={cx('animate-spin', className)} width={size} height={size} viewBox="0 0 24 24" fill="none" role="status" aria-label="Loading">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cx('animate-pulse rounded-lg bg-surface-2', className ?? 'h-5 w-full')} aria-hidden="true" />;
}

export function EmptyState({
  icon = 'clipboard',
  title,
  description,
  action,
}: {
  icon?: IconName;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-border-strong/70 bg-surface/60 px-6 py-11 text-center">
      <div className="flex size-12 items-center justify-center rounded-xl border border-border bg-surface text-fg shadow-2xs">
        <Icon name={icon} size={22} />
      </div>
      <h3 className="font-display text-base font-bold tracking-tight">{title}</h3>
      {description && <p className="max-w-sm text-sm text-muted">{description}</p>}
      {action}
    </div>
  );
}

export type Tone = 'neutral' | 'info' | 'success' | 'warning' | 'danger' | 'brand';

const BANNER: Record<Tone, string> = {
  neutral: 'bg-surface-2/85 text-fg border-border border-s-border-strong',
  info: 'bg-info-soft text-info border-info/25 border-s-info',
  success: 'bg-success-soft text-success border-success/25 border-s-success',
  warning: 'bg-warning-soft text-warning border-warning/30 border-s-warning',
  danger: 'bg-danger-soft text-danger border-danger/25 border-s-danger',
  brand: 'bg-brand-soft text-brand border-brand/25 border-s-brand',
};

const BANNER_ICON: Record<Tone, IconName> = {
  neutral: 'info',
  info: 'info',
  success: 'checkCircle',
  warning: 'alert',
  danger: 'alertCircle',
  brand: 'info',
};

export function Banner({
  tone = 'info',
  title,
  children,
  action,
  onDismiss,
  dismissLabel = 'Dismiss',
  className,
}: {
  tone?: Tone;
  title?: string;
  children?: ReactNode;
  action?: ReactNode;
  onDismiss?: () => void;
  dismissLabel?: string;
  className?: string;
}) {
  return (
    <div role={tone === 'danger' ? 'alert' : 'status'} className={cx('flex items-start gap-3 rounded-xl border border-s-4 px-4 py-3', BANNER[tone], className)}>
      <Icon name={BANNER_ICON[tone]} size={18} className="mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1 text-sm">
        {title && <p className="font-semibold tracking-tight">{title}</p>}
        {children && <div className="text-fg/90">{children}</div>}
        {action && <div className="mt-2.5">{action}</div>}
      </div>
      {onDismiss && (
        <button type="button" onClick={onDismiss} aria-label={dismissLabel} className="tap -m-2 flex items-center justify-center rounded-lg hover:bg-black/5">
          <Icon name="x" size={16} />
        </button>
      )}
    </div>
  );
}
